import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import {
  DEFAULT_SORT,
  getSearchFilterOptions,
  isSortKey,
  searchVehicles,
  SORT_OPTIONS,
  type SearchResultRow,
} from "@/lib/vehicle-search";
import { ALL_DATA_TYPES, DATA_TYPES, type DataTypeKey } from "@/lib/vehicle-data";
import { parseVehicleQuery } from "@/lib/query-intent";
import { breadcrumbLd, itemListLd, SITE_OG_IMAGE } from "@/lib/seo";


interface SearchParams {
  q?: string | undefined;
  make?: string | undefined;
  model?: string | undefined;
  year?: number | undefined;
  type?: string | undefined;
  sort?: string | undefined;
  page?: number | undefined;
}

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

export const Route = createFileRoute("/search")({
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    const yearRaw = Number(search["year"]);
    const pageRaw = Number(search["page"]);
    // Empty values are returned as undefined so the router keeps the URL clean
    // (/search rather than /search?q=&make=...&page=1). That keeps the canonical
    // hub URL directly fetchable and indexable.
    const page = Number.isFinite(pageRaw) && pageRaw > 1 ? Math.min(pageRaw, 500) : undefined;
    return {
      q: str(search["q"]).slice(0, 60) || undefined,
      make: str(search["make"]).slice(0, 60) || undefined,
      model: str(search["model"]).slice(0, 60) || undefined,
      year: Number.isFinite(yearRaw) && yearRaw > 1900 ? yearRaw : undefined,
      type: str(search["type"]).slice(0, 30) || undefined,
      // Sort is omitted from the URL when it is the default so the canonical
      // /search URL stays clean.
      sort: isSortKey(str(search["sort"])) && str(search["sort"]) !== DEFAULT_SORT
        ? str(search["sort"])
        : undefined,
      page,
    };
  },
  loaderDeps: ({ search }) => search,
  loader: async ({ deps }) => {
    // "tacoma towing" style queries: pull the attribute word out of the free
    // text so the vehicle terms still match, and use it as the data type.
    const intent = parseVehicleQuery(deps.q ?? "");
    const effectiveQ = intent.dataType ? intent.vehicleQuery : (deps.q ?? "");
    const effectiveType = deps.type || intent.dataType || "";
    const effectiveYear = deps.year || intent.year || undefined;
    const [options, results] = await Promise.all([
      getSearchFilterOptions({ data: deps.make ? { make: deps.make } : {} }),
      searchVehicles({
        data: {
          ...(effectiveQ ? { q: effectiveQ } : {}),
          ...(deps.make ? { make: deps.make } : {}),
          ...(deps.model ? { model: deps.model } : {}),
          ...(effectiveYear ? { year: effectiveYear } : {}),
          ...(effectiveType ? { dataType: effectiveType } : {}),
          sort: deps.sort ?? DEFAULT_SORT,
          page: deps.page ?? 1,
        },
      }),
    ]);
    return {
      options,
      results,
      intent: {
        dataType: intent.dataType,
        matchedPhrase: intent.matchedPhrase,
        vehicleQuery: intent.vehicleQuery,
        year: intent.year,
      },
    };
  },

  component: SearchPage,
  head: ({ match, loaderData }) => {
    const s = match.search as SearchParams;
    const rows = (loaderData?.results.results ?? []) as SearchResultRow[];
    const filtered =
      Boolean(s.q || s.make || s.model || s.year || s.type) || (s.page ?? 1) > 1;
    return {
      meta: [
        { title: "Search Vehicle Specs by Make & Model | AutoSpecData" },
        {
          name: "description",
          content:
            "Search and filter thousands of vehicles by make, model, year, and spec type — MPG, engine, transmission, drivetrain, and EPA vehicle class.",
        },
        { property: "og:title", content: "Search Vehicle Specs — AutoSpecData" },
        {
          property: "og:description",
          content:
            "Filter thousands of vehicles by make, model, year, and spec type to find verified MPG, engine, transmission, and drivetrain data.",
        },
        { property: "og:type", content: "website" },
        { property: "og:url", content: "https://autospecdata.com/search" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: SITE_OG_IMAGE },
        { name: "twitter:image", content: SITE_OG_IMAGE },
        // The clean /search hub is indexable; filtered/paginated permutations are not.
        {
          name: "robots",
          content: filtered ? "noindex, follow" : "index, follow",
        },
      ],
      links: [{ rel: "canonical", href: "https://autospecdata.com/search" }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbLd([
              { name: "Home", path: "/" },
              { name: "Search", path: "/search" },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SearchResultsPage",
            name: "Search vehicle specifications",
            url: "https://autospecdata.com/search",
            isPartOf: { "@type": "WebSite", name: "AutoSpecData", url: "https://autospecdata.com/" },
            inLanguage: "en-US",
            potentialAction: {
              "@type": "SearchAction",
              target: "https://autospecdata.com/search?q={search_term_string}",
              "query-input": "required name=search_term_string",
            },
          }),
        },
        ...(rows.length
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify(
                  itemListLd(
                    "Vehicle search results",
                    rows.map((v) => ({
                      name: `${v.year} ${v.models.makes.name} ${v.models.name}`,
                      path: `/${v.models.makes.slug}/${v.models.slug}`,
                    })),
                  ),
                ),
              },
            ]
          : []),
      ],
    };
  },
});

const selectClass =
  "h-10 w-full rounded-md border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring";

/** Page numbers around the current page, with nulls marking elided ranges. */
function pageWindow(current: number, total: number): (number | null)[] {
  const out: (number | null)[] = [];
  const push = (n: number) => {
    if (n >= 1 && n <= total && !out.includes(n)) out.push(n);
  };
  push(1);
  if (current - 2 > 2) out.push(null);
  for (let n = current - 1; n <= current + 1; n++) push(n);
  if (current + 2 < total - 1) out.push(null);
  push(total);
  return out;
}

function SearchPage() {
  const { options, results, intent } = Route.useLoaderData();
  const rawSearch = Route.useSearch();
  const search = {
    q: rawSearch.q ?? "",
    make: rawSearch.make ?? "",
    model: rawSearch.model ?? "",
    year: rawSearch.year,
    type: rawSearch.type ?? "",
    sort: rawSearch.sort ?? DEFAULT_SORT,
    page: rawSearch.page ?? 1,
  };
  const navigate = useNavigate({ from: "/search" });
  const [term, setTerm] = useState(search.q);

  useEffect(() => {
    setTerm(search.q);
  }, [search.q]);

  // Debounced free-text search
  useEffect(() => {
    if (term === search.q) return;
    const id = setTimeout(() => {
      navigate({ search: (prev: SearchParams) => ({ ...prev, q: term, page: 1 }) });
    }, 350);
    return () => clearTimeout(id);
  }, [term, search.q, navigate]);

  const update = (patch: Partial<SearchParams>) =>
    navigate({ search: (prev: SearchParams) => ({ ...prev, ...patch, page: 1 }) });

  // The attribute word parsed out of the query acts as the data type when the
  // user hasn't picked one from the filters.
  const effectiveType = search.type || intent.dataType || "";
  const totalPages = Math.max(1, Math.ceil(results.total / results.pageSize));

  const activeFilters =
    (search.q ? 1 : 0) +
    (search.make ? 1 : 0) +
    (search.model ? 1 : 0) +
    (search.year ? 1 : 0) +
    (search.type ? 1 : 0);

  // Same model + year can return several rows (trim / engine / drivetrain
  // variants). Number them so a repeated vehicle name doesn't read as a bug.
  const configPosition = new Map<string, number>();
  const configSeen = new Map<string, number>();
  for (const v of results.results) {
    const key = `${v.models.slug}-${v.year}`;
    const n = (configSeen.get(key) ?? 0) + 1;
    configSeen.set(key, n);
    configPosition.set(v.id, n);
  }

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
      <nav className="text-sm text-muted-foreground mb-4">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">Search</span>
      </nav>

      <h1 className="font-heading text-3xl font-bold text-foreground">
        Search vehicle specifications
      </h1>
      <p className="mt-2 text-muted-foreground">
        Filter verified records by make, model, year, and spec type — MPG, engine,
        transmission, drivetrain, and EPA vehicle class.
      </p>

      {/* Filters */}
      <div className="mt-6 rounded-lg border border-border bg-card p-4">
        <label htmlFor="vehicle-search" className="sr-only">
          Search by make or model
        </label>
        <input
          id="vehicle-search"
          type="search"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Search by make or model — e.g. “toyota camry”"
          className="h-11 w-full rounded-md border border-border bg-background px-4 text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />

        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label htmlFor="f-make" className="mb-1 block text-xs font-medium text-muted-foreground">
              Make
            </label>
            <select
              id="f-make"
              className={selectClass}
              value={search.make}
              onChange={(e) => update({ make: e.target.value, model: "" })}
            >
              <option value="">All makes</option>
              {options.makes.map((m: { name: string; slug: string }) => (
                <option key={m.slug} value={m.slug}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="f-model" className="mb-1 block text-xs font-medium text-muted-foreground">
              Model
            </label>
            <select
              id="f-model"
              className={selectClass}
              value={search.model}
              disabled={!search.make}
              onChange={(e) => update({ model: e.target.value })}
            >
              <option value="">{search.make ? "All models" : "Select a make first"}</option>
              {options.models.map((m: { name: string; slug: string }) => (
                <option key={m.slug} value={m.slug}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="f-year" className="mb-1 block text-xs font-medium text-muted-foreground">
              Year
            </label>
            <select
              id="f-year"
              className={selectClass}
              value={search.year ? String(search.year) : ""}
              onChange={(e) =>
                update({ year: e.target.value ? Number(e.target.value) : undefined })
              }
            >
              <option value="">All years</option>
              {options.years.map((y: number) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="f-type" className="mb-1 block text-xs font-medium text-muted-foreground">
              Data type
            </label>
            <select
              id="f-type"
              className={selectClass}
              value={search.type}
              onChange={(e) => update({ type: e.target.value })}
            >
              <option value="">All data types</option>
              {ALL_DATA_TYPES.map((k) => (
                <option key={k} value={k}>
                  {DATA_TYPES[k].shortLabel}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Quick data-type chips */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {ALL_DATA_TYPES.map((k) => {
            const active = search.type === k;
            return (
              <button
                key={k}
                type="button"
                onClick={() => update({ type: active ? "" : k })}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                  active
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-background text-muted-foreground hover:text-foreground"
                }`}
              >
                {DATA_TYPES[k].shortLabel}
              </button>
            );
          })}
          {activeFilters > 0 && (
            <button
              type="button"
              onClick={() =>
                navigate({
                  search: { q: "", make: "", model: "", year: undefined, type: "", sort: undefined, page: 1 },
                })
              }
              className="ml-auto text-xs font-medium text-primary hover:underline"
            >
              Clear all filters
            </button>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-heading text-lg font-semibold text-foreground">
          {results.total.toLocaleString()} {results.total === 1 ? "match" : "matches"}
        </h2>
        <div className="flex items-center gap-3">
          {totalPages > 1 && (
            <span className="text-sm text-muted-foreground">
              Page {results.page} of {totalPages.toLocaleString()}
            </span>
          )}
          <label htmlFor="f-sort" className="text-xs font-medium text-muted-foreground">
            Sort
          </label>
          <select
            id="f-sort"
            className="h-9 rounded-md border border-border bg-card px-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            value={search.sort}
            onChange={(e) =>
              update({ sort: e.target.value === DEFAULT_SORT ? undefined : e.target.value })
            }
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>


      {intent.dataType && (
        <div className="mt-4 rounded-lg border border-border bg-muted/40 p-4">
          <p className="text-sm text-foreground">
            Reading “{intent.matchedPhrase}” as{" "}
            <strong>{DATA_TYPES[intent.dataType as DataTypeKey].shortLabel}</strong>
            {intent.vehicleQuery ? <> for “{intent.vehicleQuery}”</> : null}
            {intent.year ? <> ({intent.year})</> : null}. Results link straight to that data.
          </p>
          {results.results.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {Array.from(
                new Map(
                  results.results.map((v: SearchResultRow) => [
                    `${v.models.makes.slug}/${v.models.slug}`,
                    v,
                  ]),
                ).values(),
              )
                .slice(0, 4)
                .map((v) => (
                  <Link
                    key={`${v.models.makes.slug}-${v.models.slug}`}
                    to="/$make/$model/$dataType"
                    params={{
                      make: v.models.makes.slug,
                      model: v.models.slug,
                      dataType: intent.dataType as string,
                    }}
                    className="rounded-full border border-primary px-3 py-1 text-xs font-medium text-primary hover:bg-primary hover:text-primary-foreground"
                  >
                    {v.models.makes.name} {v.models.name}{" "}
                    {DATA_TYPES[intent.dataType as DataTypeKey].shortLabel}
                  </Link>
                ))}
            </div>
          )}
        </div>
      )}

      {results.results.length === 0 ? (

        <p className="mt-6 rounded-lg border border-dashed border-border p-8 text-center text-muted-foreground">
          No vehicles match those filters. Try clearing the year or data type.
        </p>
      ) : (
        <div className="mt-4 overflow-x-auto rounded-lg border border-border">
          <table className="spec-table w-full text-sm">
            <thead>
              <tr>
                <th className="text-left">Vehicle</th>
                <th className="text-left">Year</th>
                <th className="text-left">Trim</th>
                <th className="text-left">{columnLabel(effectiveType)}</th>
                <th className="text-left">Specs</th>
              </tr>
            </thead>
            <tbody>
              {results.results.map((v: SearchResultRow) => {
                const makeSlug = v.models.makes.slug;
                const modelSlug = v.models.slug;
                return (
                  <tr key={v.id}>
                    <td className="font-medium">
                      <Link
                        to="/$make/$model"
                        params={{ make: makeSlug, model: modelSlug }}
                        className="text-primary hover:underline"
                      >
                        {v.models.makes.name} {v.models.name}
                      </Link>
                      {v.configCount > 1 && (
                        <span className="mt-0.5 block text-xs font-normal text-muted-foreground">
                          Configuration {configPosition.get(v.id) ?? 1} of {v.configCount} for{" "}
                          {v.year}
                        </span>
                      )}
                    </td>
                    <td className="font-mono">{v.year}</td>
                    <td className="text-muted-foreground">{v.trim ?? "—"}</td>
                    <td className="font-mono">{columnValue(v, effectiveType)}</td>
                    <td>
                      <Link
                        to="/$make/$model/$dataType"
                        params={{
                          make: makeSlug,
                          model: modelSlug,
                          dataType: (effectiveType || "mpg") as string,
                        }}
                        className="text-primary hover:underline"
                      >
                        View {DATA_TYPES[(effectiveType || "mpg") as DataTypeKey].shortLabel}
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <nav aria-label="Search results pages" className="mt-6 flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            disabled={results.page <= 1}
            onClick={() => navigate({ search: (p: SearchParams) => ({ ...p, page: (p.page ?? 1) - 1 }) })}
            className="rounded-md border border-border px-3 py-2 text-sm font-medium disabled:opacity-40"
          >
            Previous
          </button>

          {pageWindow(results.page, totalPages).map((n, i) =>
            n === null ? (
              <span key={`gap-${i}`} className="px-1 text-sm text-muted-foreground">
                …
              </span>
            ) : (
              <button
                key={n}
                type="button"
                aria-current={n === results.page ? "page" : undefined}
                onClick={() => navigate({ search: (p: SearchParams) => ({ ...p, page: n }) })}
                className={
                  n === results.page
                    ? "min-w-9 rounded-md border border-primary bg-primary px-2 py-2 text-sm font-semibold text-primary-foreground"
                    : "min-w-9 rounded-md border border-border px-2 py-2 text-sm font-medium hover:bg-secondary"
                }
              >
                {n.toLocaleString()}
              </button>
            ),
          )}

          <button
            type="button"
            disabled={results.page >= totalPages}
            onClick={() => navigate({ search: (p: SearchParams) => ({ ...p, page: (p.page ?? 1) + 1 }) })}
            className="rounded-md border border-border px-3 py-2 text-sm font-medium disabled:opacity-40"
          >
            Next
          </button>

          <form
            className="ml-2 flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              const raw = new FormData(e.currentTarget).get("jump");
              const n = Math.max(1, Math.min(totalPages, Number(raw) || 1));
              navigate({ search: (p: SearchParams) => ({ ...p, page: n }) });
            }}
          >
            <label htmlFor="jump-page" className="text-xs text-muted-foreground">
              Go to page
            </label>
            <input
              id="jump-page"
              name="jump"
              type="number"
              min={1}
              max={totalPages}
              defaultValue={results.page}
              className="h-9 w-20 rounded-md border border-border bg-card px-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <button
              type="submit"
              className="rounded-md border border-border px-3 py-2 text-sm font-medium hover:bg-secondary"
            >
              Go
            </button>
          </form>
        </nav>
      )}


      <section className="mt-12 border-t border-border pt-8">
        <h2 className="font-heading text-xl font-bold text-foreground">
          How to use this vehicle specification search
        </h2>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground">
          Every record behind this search comes from two public sources: the EPA fuel economy
          database, which supplies city, highway and combined MPG, annual fuel cost, fuel type and
          EPA vehicle class, and the NHTSA vPIC catalogue, which supplies the make, model and
          model-year identity plus body and engine descriptors. Results are grouped by variant, so
          one model year can return several rows when a manufacturer offered different engines,
          transmissions or drive layouts.
        </p>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground">
          Start broad and narrow down. Typing a make or a make-and-model phrase filters the whole
          catalogue instantly; adding a model year isolates a single generation; switching the data
          type changes the value column so you can rank the same set of vehicles by combined MPG,
          engine, transmission, drivetrain, annual fuel cost or EPA class without losing your other
          filters. Clearing the year is usually the fastest way to widen a search that returns
          nothing.
        </p>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground">
          Comparing figures fairly means comparing like with like. EPA ratings are laboratory
          results measured on a standard test cycle, so they are consistent between rows here but
          will differ from what you see on the road — cold weather, hills, roof loads, towing and
          short trips all reduce real-world economy. Annual fuel cost assumes 15,000 miles a year at
          national average fuel prices, which makes it a comparison tool rather than a budget. For
          the complete record of any vehicle, including dimensions, towing capacity, safety ratings
          and maintenance intervals, open its model page from the results table.
        </p>
      </section>
    </div>
  );
}

function columnLabel(type: string): string {
  switch (type) {
    case "engine":
      return "Engine";
    case "fuel-cost":
      return "Annual fuel cost";
    case "transmission":
      return "Transmission";
    case "drivetrain":
      return "Drivetrain";
    case "vehicle-class":
      return "Vehicle class";
    default:
      return "Combined MPG";
  }
}

function columnValue(v: SearchResultRow, type: string): string {
  switch (type) {
    case "engine":
      return v.engine_desc ?? `${v.cylinders ?? "—"} cyl ${v.displacement ?? ""}`.trim();
    case "fuel-cost":
      return v.annual_fuel_cost ? `$${v.annual_fuel_cost.toLocaleString()}/yr` : "—";
    case "transmission":
      return v.transmission ?? "—";
    case "drivetrain":
      return v.drive ?? "—";
    case "vehicle-class":
      return v.vehicle_class ?? "—";
    default:
      return v.mpg_combined ? `${v.mpg_combined} MPG` : "—";
  }
}
